/**
 * Локализация RU/EN (ТЗ §5.1).
 *
 * Своя реализация, а не i18next: словарь плоский, переменных немного, множественных
 * форм пока нет. Библиотека на 40 килобайт ради `t('nav.deals')` — это зависимость,
 * которую придётся обновлять годами ради функции в двадцать строк.
 *
 * Когда понадобятся множественные числа (`{count} сделок` против `{count} сделка`),
 * место для них — здесь, и тогда i18next станет оправданным.
 */

import { ru } from './ru'
import { en } from './en'

export type Locale = 'ru' | 'en'

const DICTIONARIES: Record<Locale, Record<string, string>> = { ru, en }

const STORAGE_KEY = 'crm.locale'

/**
 * Язык по умолчанию — русский.
 *
 * Не язык браузера: система внедряется в российской компании, и англоязычный
 * интерфейс по умолчанию у сотрудника с английской Windows — сюрприз, а не услуга.
 */
const DEFAULT_LOCALE: Locale = 'ru'

export function currentLocale(): Locale {
  const stored = localStorage.getItem(STORAGE_KEY)
  return stored === 'ru' || stored === 'en' ? stored : DEFAULT_LOCALE
}

export function setLocale(locale: Locale): void {
  localStorage.setItem(STORAGE_KEY, locale)
  document.documentElement.lang = locale
}

/**
 * Переводит ключ.
 *
 * Отсутствующий ключ возвращается как есть, а не пустой строкой: `nav.deals` на экране
 * сразу виден и чинится, пустое место выглядит как задуманная пустота и живёт годами.
 */
export function translate(
  key: string,
  locale: Locale = currentLocale(),
  params?: Record<string, string | number>,
): string {
  const template = DICTIONARIES[locale][key] ?? DICTIONARIES[DEFAULT_LOCALE][key] ?? key
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  )
}

/** Ключи, которых нет в переводе, — для проверки полноты словаря тестом. */
export function missingKeys(locale: Locale): string[] {
  const reference = Object.keys(DICTIONARIES[DEFAULT_LOCALE])
  const target = DICTIONARIES[locale]
  return reference.filter((key) => !(key in target))
}
